/**
 * Auth-V2 API Client
 * Handles authentication against the authentication-v2 backend service
 */

import { UnauthorizedException } from '@40seas/errors';
import { z } from 'zod';
import { AuthTokens, AuthTokensSchema, AuthUser, AuthUserSchema, LoginResponse, RegistrationResponse } from './types';
import { authStorage } from './storage';

const OAuthUrlResponseSchema = z.object({
    authUrl: z.string().url('authUrl must be a valid URL'),
});

export interface AuthClientConfig {
    baseUrl?: string;
}

class AuthClientClass {
    private baseUrl: string;
    // Tracks in-flight refresh request to prevent concurrent refresh token calls
    private refreshPromise: Promise<AuthTokens> | null = null;
    private readonly FORCE_PASSWORD_RESET_KEY = 'self_managed_password_reset_redirect_required';

    constructor(config?: AuthClientConfig) {
        this.baseUrl = config?.baseUrl || process.env.AUTH_V2_URL || 'http://localhost:3334';
    }

    /**
     * Get the configured base URL
     */
    getBaseUrl(): string {
        return this.baseUrl;
    }

    /**
     * Configure the auth client base URL
     */
    configure(config: AuthClientConfig): void {
        if (config.baseUrl) {
            this.baseUrl = config.baseUrl;
        }
    }

    /**
     * Login with email and password
     */
    async loginWithPassword(email: string, password: string): Promise<LoginResponse> {
        localStorage.removeItem(this.FORCE_PASSWORD_RESET_KEY);

        const response = await fetch(`${this.baseUrl}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            if (response.status === 418) {
                localStorage.setItem(this.FORCE_PASSWORD_RESET_KEY, 'true');
            }
            const errorMessage = await this.extractErrorMessage(response, 'Login failed');
            throw new Error(errorMessage);
        }

        localStorage.removeItem(this.FORCE_PASSWORD_RESET_KEY);

        const data = await response.json();
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        // Store user before tokens to ensure user data is available when auth-tokens-changed event fires
        authStorage.storeUser(user);
        authStorage.storeTokens(tokens);

        return { user, tokens };
    }

    /**
     * Register a new user with email and password
     */
    async register(email: string, password: string, name?: string): Promise<RegistrationResponse> {
        const response = await fetch(`${this.baseUrl}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password, name }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Registration failed');
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return {
            message: data.message || 'Registration successful! Please check your email to verify your account.',
            email: data.email,
            requiresVerification: true,
        };
    }

    /**
     * Get Google OAuth authorization URL
     */
    async getGoogleAuthUrl(returnTo?: string): Promise<string> {
        const params = new URLSearchParams();
        if (returnTo) {
            params.set('returnTo', returnTo);
        }
        const queryString = params.toString();
        const url = `${this.baseUrl}/auth/google${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to get Google auth URL');
        }
        const data = await response.json();
        return this.validateOAuthUrlResponse(data);
    }

    /**
     * Handle Google OAuth callback
     */
    async handleGoogleCallback(code: string, state?: string): Promise<LoginResponse> {
        const response = await fetch(`${this.baseUrl}/auth/google/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Google authentication failed');
            throw new Error(errorMessage);
        }

        const data = await response.json();
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        authStorage.storeUser(user);
        authStorage.storeTokens(tokens);

        return { user, tokens, isNewUser: data.isNewUser || false };
    }

    /**
     * Get Microsoft OAuth authorization URL
     */
    async getMicrosoftAuthUrl(returnTo?: string): Promise<string> {
        const params = new URLSearchParams();
        if (returnTo) {
            params.set('returnTo', returnTo);
        }
        const queryString = params.toString();
        const url = `${this.baseUrl}/auth/microsoft${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(url, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error('Failed to get Microsoft auth URL');
        }
        const data = await response.json();
        return this.validateOAuthUrlResponse(data);
    }

    /**
     * Handle Microsoft OAuth callback
     */
    async handleMicrosoftCallback(code: string, state?: string): Promise<LoginResponse> {
        const response = await fetch(`${this.baseUrl}/auth/microsoft/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Microsoft authentication failed');
            throw new Error(errorMessage);
        }

        const data = await response.json();
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        authStorage.storeUser(user);
        authStorage.storeTokens(tokens);

        return { user, tokens, isNewUser: data.isNewUser || false };
    }

    /**
     * Verify email with 6-digit code and auto-login
     */
    async verifyCode(email: string, code: string): Promise<LoginResponse> {
        const response = await fetch(`${this.baseUrl}/auth/verify-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, code }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Verification failed');
            throw new Error(errorMessage);
        }

        const data = await response.json();
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        authStorage.storeUser(user);
        authStorage.storeTokens(tokens);

        return { user, tokens };
    }

    /**
     * Resend email verification
     */
    async resendVerification(email: string): Promise<{ message: string }> {
        const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Failed to resend verification email');
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    /**
     * Request a password reset code
     */
    async requestPasswordReset(email: string): Promise<{ message: string }> {
        const response = await fetch(`${this.baseUrl}/auth/password-reset/request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Failed to request password reset');
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    /**
     * Validate a password reset code (read-only check)
     */
    async validatePasswordResetCode(email: string, code: string): Promise<{ valid: boolean }> {
        const response = await fetch(`${this.baseUrl}/auth/password-reset/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, code }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Failed to validate reset code');
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    /**
     * Reset password with a valid code
     */
    async resetPassword(email: string, code: string, password: string): Promise<{ message: string }> {
        const response = await fetch(`${this.baseUrl}/auth/password-reset/change`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, code, password }),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Failed to reset password');
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    /**
     * Refresh the access token using the refresh token.
     * Uses request deduplication to prevent race conditions.
     */
    async refreshToken(): Promise<AuthTokens> {
        if (this.refreshPromise !== null) {
            return this.refreshPromise;
        }

        const refreshToken = authStorage.getRefreshToken();

        // Attempt refresh with localStorage token first (backward compatibility).
        // If it fails (e.g. stale rotated token), retry once with cookie-only payload.
        this.refreshPromise = this.executeRefreshWithCookieFallback(refreshToken ?? undefined);

        try {
            return await this.refreshPromise;
        } finally {
            this.refreshPromise = null;
        }
    }

    /**
     * Logout and revoke tokens
     */
    async logout(): Promise<void> {
        const refreshToken = authStorage.getRefreshToken();
        try {
            await fetch(`${this.baseUrl}/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(refreshToken ? { refreshToken } : {}),
            });
        } catch (error) {
            console.warn('Failed to revoke refresh token on logout:', error);
        }
        authStorage.clearAll();
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return authStorage.isAuthenticated();
    }

    /**
     * Check if token is expired
     */
    isTokenExpired(): boolean {
        return authStorage.isTokenExpired();
    }

    /**
     * Get current access token
     */
    getAccessToken(): string | null {
        return authStorage.getAccessToken();
    }

    /**
     * Get current user
     */
    getUser(): AuthUser | null {
        return authStorage.getUser();
    }

    /**
     * Clear all stored auth data
     */
    clearAuth(): void {
        authStorage.clearAll();
    }

    // Private methods

    private async executeRefreshWithCookieFallback(refreshToken?: string): Promise<AuthTokens> {
        try {
            return await this.executeRefresh(refreshToken, !refreshToken);
        } catch (error) {
            // If a stored refresh token exists and fails (common after cross-subdomain
            // navigation with token rotation), retry once with cookie-only refresh.
            // Retry only for 401 auth failures; do not retry on transient server/network errors.
            if (refreshToken && error instanceof UnauthorizedException) {
                return await this.executeRefresh(undefined);
            }

            throw error;
        }
    }

    private async executeRefresh(refreshToken?: string, clearStorageOnFailure = true): Promise<AuthTokens> {
        const refreshUrl = `${this.baseUrl}/auth/refresh`;

        const response = await fetch(refreshUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        });

        if (!response.ok) {
            const errorMessage = await this.extractErrorMessage(response, 'Token refresh failed');
            if (clearStorageOnFailure) {
                authStorage.clearAll();
            }
            if (response.status === 401) {
                throw new UnauthorizedException({
                    message: errorMessage,
                    code: 'TOKEN_REFRESH_FAILED',
                });
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        const tokens = this.extractTokens(data);

        // Extract user data from the JWT access token payload so that cookie-only
        // refresh (cross-subdomain) populates localStorage with the user profile.
        if (!authStorage.getUser()) {
            const user = this.extractUserFromAccessToken(tokens.accessToken);
            if (user) {
                authStorage.storeUser(user);
            }
        }

        authStorage.storeTokens(tokens);
        return tokens;
    }

    /**
     * Decode the JWT access token (without verification) and extract user data.
     * The backend signs access tokens with RS256; we only need the payload here.
     */
    private extractUserFromAccessToken(accessToken: string): AuthUser | null {
        try {
            const base64Url = accessToken.split('.')[1];
            // JWT payloads use base64url encoding (- instead of +, _ instead of /)
            // Convert to standard base64 before decoding
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(base64));
            return AuthUserSchema.parse({
                id: payload.sub,
                email: payload.email,
                name: payload.name ?? null,
                picture: payload.picture ?? null,
                roles: payload.roles ?? null,
            });
        } catch {
            return null;
        }
    }

    private async extractErrorMessage(response: Response, defaultMessage: string): Promise<string> {
        try {
            const error = await response.json();
            return error.message || defaultMessage;
        } catch {
            return `${defaultMessage}: ${response.status} ${response.statusText}`;
        }
    }

    private extractTokens(data: Record<string, unknown>): AuthTokens {
        return AuthTokensSchema.parse(data);
    }

    private validateUser(user: unknown): AuthUser {
        return AuthUserSchema.parse(user);
    }

    private validateOAuthUrlResponse(data: unknown): string {
        const result = OAuthUrlResponseSchema.safeParse(data);
        if (!result.success) {
            throw new Error(`Invalid OAuth URL response: ${result.error.message}`);
        }
        return result.data.authUrl;
    }
}

/**
 * Singleton instance of the auth client
 */
export const authClient = new AuthClientClass();
